/**
 * V2 Profile showcase pages (2026-07-13, the "cleaner profile" round):
 * Trophy case, Collections, and Lists each broke out of the profile to
 * their own shareable URL — /user/{username}/trophies, /collections,
 * /lists — exactly the way Geography always did (/user/{username}/map).
 * The profile hero keeps a row of doors with counts; these pages hold
 * the full content. One module, thin views over shared chrome.
 * Categories joined the row 2026-07-17: every category the explorer has
 * YOINKED in (the journal's stacks landing as an index), at
 * /user/{username}/categories.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { shareButton, handleShareClick } from '../../ui/share.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import {
  fetchProfile, fetchTrophies, fetchStartedSets, fetchAllUserYoinks,
  deriveCategoryStacks, TROPHY_MEDALS, trophyMedal, medalTally,
  fetchNearTrophies, deriveNearTrophies,
  fetchTrophyGaps, deriveTrophyGaps,
} from './profile.js';
import {
  fetchMilestones, fetchEarnedAwardRows, deriveAwards, journalStats, earnedWhen,
} from './awards.js';
import { fetchPublicLists } from '../lists/lists.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';

const KINDS = {
  trophies: {
    title: (name) => `${name}’s trophy case`,
    hint: 'Top-3 collector standings. Tap a medal to see the category and its leaderboard.',
    async body(profile, ctx) {
      // Your own case also scouts what's climbable (2026-08-07, the
      // awards-excitement round): ranks 2-6 with next-rung-up targets
      // from get_user_trophy_gaps, falling back to the older
      // get_user_near_trophies (ranks 4-6 → bronze) until that SQL
      // runs. Visitors never fetch either; a missing RPC stays away.
      const own = ctx.store.get().user?.id === profile.id;
      const [rows, gapRows] = await Promise.all([
        fetchTrophies(profile.id),
        own ? fetchTrophyGaps(profile.id).catch(() => null) : null,
      ]);
      // Fall back to the old RPC only when the new one is missing —
      // both aggregate the whole yoinks table, so never pay for both
      const nearRows = own && gapRows === null
        ? await fetchNearTrophies(profile.id).catch(() => [])
        : [];
      // Same shape guard as the profile hero: the RPC is optional and
      // only rows that actually look like trophies may render
      const medals = (rows || [])
        .filter((t) => TROPHY_MEDALS[t.rank] && t.category_name)
        .sort((a, b) => a.rank - b.rank || String(a.category_name).localeCompare(String(b.category_name)));
      // One labeled shelf per metal, count in the header — the metal is
      // told by TEXT, not by squinting at 15px of tint (2026-08-07 iPad
      // report: "hard to tell Gold from Silver from Bronze")
      const tally = medalTally(medals);
      const shelvesHtml = medals.length ? [1, 2, 3].map((rank) => {
        const metal = TROPHY_MEDALS[rank];
        const shelf = medals.filter((t) => t.rank === rank);
        if (!shelf.length) return '';
        return `
        <section class="trophy-shelf" aria-label="${metal} medals">
          <h3 class="trophy-shelf-title">${trophyMedal(rank, 20)} ${metal.charAt(0).toUpperCase()}${metal.slice(1)}
            <span class="trophy-shelf-count">${shelf.length}</span></h3>
          <div class="trophy-chips trophy-chips-page">
            ${shelf.map((t) => `
              <button type="button" class="trophy-chip trophy-rank-${t.rank}" data-cat-slug="${slugify(t.category_name)}">
                ${trophyMedal(t.rank, 22)}
                <span>${escapeHtml(t.category_name)}</span>
              </button>`).join('')}
          </div>
        </section>`;
      }).join('')
        : '<p class="log-hint">No podium finishes yet — pick a category and start collecting.</p>';
      const tallyHtml = `
        <p class="trophy-tally">
          ${medals.length ? `<span class="trophy-tally-counts">${[1, 2, 3].map((rank) =>
            `<span class="trophy-tally-metal">${trophyMedal(rank, 16)} ${tally[TROPHY_MEDALS[rank]]} ${TROPHY_MEDALS[rank]}</span>`).join(' · ')}</span>` : ''}
          <button type="button" class="v2-btn-more trophy-tally-link" data-medal-table>Overall medal table →</button>
        </p>`;
      // The strip's two groups: medal upgrades (rank 2-3) lead, the
      // podium hunt (rank 4-6) follows. Pre-migration fallback renders
      // the old single bronze-gap group.
      const gaps = gapRows !== null ? deriveTrophyGaps(gapRows) : null;
      const leads = (gaps || []).filter((t) => t.rank <= 3);
      const podium = gaps !== null
        ? gaps.filter((t) => t.rank >= 4)
        : deriveNearTrophies(nearRows).map((t) => ({ ...t, targetRank: 3 }));
      const gapRow = (t) => `
        <button type="button" class="collection-row" data-cat-slug="${slugify(t.name)}">
          <span class="collection-name">${categoryIcon({ id: t.categoryId, name: t.name }, 15)} ${escapeHtml(t.name)}</span>
          <span class="collection-progress">${t.rank <= 3 ? `${trophyMedal(t.rank)} ${TROPHY_MEDALS[t.rank]} now` : `#${t.rank}`}
            — ${t.gap} more place${t.gap === 1 ? '' : 's'} for ${trophyMedal(t.targetRank)} ${TROPHY_MEDALS[t.targetRank]}</span>
        </button>`;
      const nearHtml = (leads.length || podium.length) ? `
        <section class="trophy-near" aria-label="Within reach">
          ${leads.length ? `
            <h3 class="friends-title">Take the lead</h3>
            <div class="collection-rows">${leads.map(gapRow).join('')}</div>` : ''}
          ${podium.length ? `
            <h3 class="friends-title">Within reach</h3>
            <div class="collection-rows">${podium.map(gapRow).join('')}</div>` : ''}
          <p class="log-hint">Only you see these — tap one to scout the podium.</p>
        </section>` : '';
      return tallyHtml + shelvesHtml + nearHtml;
    },
  },
  awards: {
    title: (name) => `${name}’s awards`,
    hint: 'Milestone badges — earned by exploring, never lost to a leaderboard.',
    async body(profile, ctx) {
      // Most families derive from data the profile already cached; the
      // RPC pair and the recorded-tier rows degrade to absent (lists
      // pattern) until their SQL runs
      const [yoinks, sets, milestones, earnedRows] = await Promise.all([
        fetchAllUserYoinks(profile.id),
        fetchStartedSets(profile.id).catch(() => []),
        fetchMilestones(profile.id).catch(() => []),
        fetchEarnedAwardRows(profile.id).catch(() => []),
      ]);
      const all = deriveAwards({
        yoinkCount: yoinks.length, sets, milestones,
        stats: journalStats(yoinks), earnedRows,
      });
      const own = ctx.store.get().user?.id === profile.id;
      // Visitors see the earned shelf; your own page also shows what's
      // still out there — the next tier is the motivation
      const shown = own ? all : all.filter((a) => a.earned);
      if (!shown.length) return '<p class="log-hint">No awards yet — every journal starts with one yoink.</p>';
      return `
        <div class="award-cards">
          ${shown.map((a) => {
            const when = a.earned ? earnedWhen(a.earnedAt) : '';
            return `
            <div class="award-card award-${a.color}${a.earned ? '' : ' award-locked'}">
              <span class="award-art" aria-hidden="true">${icon(a.icon, 46)}</span>
              <span class="award-name">${escapeHtml(a.name)}</span>
              <span class="award-line">${escapeHtml(a.earned ? a.line : `Not earned yet — ${a.hint.charAt(0).toLowerCase()}${a.hint.slice(1)}`)}</span>
              ${when ? `<span class="award-date">Earned ${escapeHtml(when)}</span>` : ''}
              ${a.earned && a.nextAt ? `<span class="award-next">Next at ${a.nextAt}</span>` : ''}
            </div>`;
          }).join('')}
        </div>`;
    },
  },
  collections: {
    title: (name) => `${name}’s collections`,
    hint: 'Started collections and how far along each one is. Tap one for the checklist.',
    async body(profile) {
      const sets = await fetchStartedSets(profile.id);
      if (!sets.length) return '<p class="log-hint">No collections started yet.</p>';
      return `
        <div class="collection-rows">
          ${sets.map((set) => {
            const pct = set.total_count ? Math.round((set.completed_count / set.total_count) * 100) : 0;
            return `
              <button type="button" class="collection-row" data-cat-slug="${slugify(set.set_name || '')}">
                <span class="collection-name">${categoryIcon({ id: set.set_id, name: set.set_name }, 15)} ${escapeHtml(set.set_name || '')}</span>
                <span class="collection-progress">
                  <span class="collection-bar"><span class="collection-fill" style="width:${pct}%"></span></span>
                  ${set.completed_count} of ${set.total_count}
                </span>
              </button>`;
          }).join('')}
        </div>`;
    },
  },
  categories: {
    title: (name) => `${name}’s categories`,
    hint: 'Every category in this explorer’s journal, most places first. Tap one to see those yoinks.',
    async body(profile) {
      // The same public journal the profile's stacks landing derives
      // from — both fetches are cached, so arriving from the profile
      // costs nothing extra. Trophies are garnish: no RPC, no medals.
      const [yoinks, trophies] = await Promise.all([
        fetchAllUserYoinks(profile.id),
        fetchTrophies(profile.id).catch(() => []),
      ]);
      const stacks = deriveCategoryStacks(yoinks, trophies);
      if (!stacks.length) return '<p class="log-hint">No yoinks logged yet.</p>';
      return `
        <div class="collection-rows">
          ${stacks.map((stack) => {
            const medal = trophyMedal(stack.trophyRank);
            return `
              <button type="button" class="collection-row" data-journal-cat="${slugify(stack.name)}">
                <span class="collection-name">${categoryIcon(stack.category, 15)} ${escapeHtml(stack.name)}${medal ? ` ${medal}` : ''}</span>
                <span class="collection-progress">${stack.places} place${stack.places === 1 ? '' : 's'}</span>
              </button>`;
          }).join('')}
        </div>`;
    },
  },
  lists: {
    title: (name) => `${name}’s lists`,
    hint: 'Public lists of places worth a detour.',
    async body(profile, ctx) {
      const lists = await fetchPublicLists(profile.id);
      const own = ctx.store.get().user?.id === profile.id;
      const rows = lists.map((list) => `
        <button type="button" class="collection-row" data-list-slug="${escapeHtml(list.slug)}">
          <span class="collection-name">${list.featured ? '<span class="featured-star" title="Picked by the rangers" aria-label="Featured">★</span> ' : ''}${escapeHtml(list.title)}</span>
          <span class="collection-progress">${list.entry_count} place${list.entry_count === 1 ? '' : 's'}</span>
        </button>`).join('');
      return `
        ${rows ? `<div class="collection-rows">${rows}</div>` : '<p class="log-hint">No public lists yet.</p>'}
        ${own ? '<button type="button" class="v2-btn-more" data-my-lists>Manage my lists</button>' : ''}`;
    },
  },
};

function makeView(kind) {
  const spec = KINDS[kind];
  return {
    mount(el, ctx) {
      const { router, params } = ctx;
      let destroyed = false;
      let username = null;
      const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

      el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

      (async () => {
        try {
          const profile = await fetchProfile(params.username);
          if (destroyed) return;
          if (!profile) {
            el.innerHTML = '<div class="v2-empty">No explorer by that name.</div>';
            return;
          }
          username = profile.username;
          const name = profile.full_name || profile.username;
          const title = spec.title(name);
          document.title = `${title} | Yoink Adventures v2`;
          setRibbonContext(`${name} keeps their adventures here — photos, pins and collections. Start yours.`);
          const body = await spec.body(profile, ctx);
          if (destroyed) return;
          el.innerHTML = `
            <nav class="v2-back" aria-label="Back">
              <a href="${basePath}/user/${escapeHtml(username)}" data-showcase-back>← ${escapeHtml(name)}’s journal</a>
              ${shareButton(`${title} on Yoink Adventures`)}
            </nav>
            <header class="v2-category-head">
              <div class="cat-head-names">
                <h2 class="cat-head-name">${escapeHtml(title)}</h2>
                <p class="cat-head-desc">${spec.hint}</p>
              </div>
            </header>
            <section class="showcase-body showcase-${kind}">${body}</section>`;
        } catch {
          if (destroyed) return;
          el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
        }
      })();

      function onClick(event) {
        if (handleShareClick(event)) return;
        if (event.target.closest('[data-showcase-back]')) {
          event.preventDefault();
          router.navigate('user', { username: username || params.username });
          return;
        }
        const journalCat = event.target.closest('[data-journal-cat]');
        if (journalCat) {
          // Their journal filtered to that category — the profile's
          // ?category= deep link (V1 user-panel.js:85-92 convention)
          router.navigate('user', { username: username || params.username },
            { query: { category: journalCat.dataset.journalCat } });
          return;
        }
        const cat = event.target.closest('[data-cat-slug]');
        if (cat) {
          router.navigate('category', { slug: cat.dataset.catSlug });
          return;
        }
        const list = event.target.closest('[data-list-slug]');
        if (list) {
          router.navigate('list', { username, slug: list.dataset.listSlug });
          return;
        }
        if (event.target.closest('[data-medal-table]')) {
          router.navigate('medals');
          return;
        }
        if (event.target.closest('[data-my-lists]')) router.navigate('lists');
      }

      el.addEventListener('click', onClick);
      return () => {
        destroyed = true;
        el.removeEventListener('click', onClick);
      };
    },
  };
}

export const trophies = makeView('trophies');
export const userAwards = makeView('awards');
export const collections = makeView('collections');
export const userCategories = makeView('categories');
export const userLists = makeView('lists');
